import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { AbcClass, AbcProduct } from "@/hooks/use-analytics";
import { cn, formatNumber } from "@/lib/utils";

type Metric = "revenue" | "quantity";
type SortKey = "revenue" | "quantity" | "cumulativeShare" | "stock" | "coverageDays";
type ClassFilter = "all" | AbcClass;

// Prihod u tabeli ide bez simbola valute (kolona je već označena s „PRIHOD (KM)"),
// pa brojevi u koloni ostaju uski i poravnati.
const fmtNumber = (v: number) => formatNumber(v);

// Iste boje klase kao u „Struktura asortimana" i Pareto grafikonu.
const CLASS_DOT: Record<AbcClass, string> = {
  A: "bg-emerald-500",
  B: "bg-amber-500",
  C: "bg-slate-400",
};

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "revenue", label: "Prihod (KM)" },
  { key: "quantity", label: "Komadi" },
  { key: "cumulativeShare", label: "Kum. %" },
  { key: "stock", label: "Zaliha" },
  { key: "coverageDays", label: "Pokrivenost" },
];

// Sort prati prekidač metrike, ali samo ako je aktivna kolona metrička (Prihod/Komadi).
// Ručno odabrana nemetrička kolona (Kum. %, Zaliha, Pokrivenost) se zadržava.
export function sortKeyForMetric(sortKey: SortKey, metric: Metric): SortKey {
  return sortKey === "revenue" || sortKey === "quantity" ? metric : sortKey;
}

function sortValue(p: AbcProduct, key: SortKey): number | null {
  switch (key) {
    case "revenue": return p.revenue;
    case "quantity": return p.quantity;
    case "cumulativeShare": return p.cumulativeShare;
    case "stock": return p.stock;
    case "coverageDays": return p.coverageDays;
  }
}

function coverageTone(days: number): string {
  if (days > 30) return "bg-emerald-500";
  if (days >= 7) return "bg-amber-500";
  return "bg-red-500";
}

export function AbcTable({ products, metric }: { products: AbcProduct[]; metric: Metric }) {
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState<ClassFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>(metric);
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Kad se metrika promijeni, sort je prati (vidi `sortKeyForMetric`) — inače bi
  // „Kum. %" mjerio jedno, a redoslijed dolazio od druge metrike. Izvedeno stanje se
  // računa u renderu (React preporuka za „state derived from props"), bez useEffect-a.
  const [prevMetric, setPrevMetric] = useState<Metric>(metric);
  if (prevMetric !== metric) {
    setPrevMetric(metric);
    setSortKey(sortKeyForMetric(sortKey, metric));
  }

  const q = search.trim().toLowerCase();
  const searched = q
    ? products.filter((p) =>
        p.name.toLowerCase().includes(q) || (p.sku ?? "").toLowerCase().includes(q))
    : products;

  const counts: Record<ClassFilter, number> = {
    all: searched.length,
    A: searched.filter((p) => p.abcClass === "A").length,
    B: searched.filter((p) => p.abcClass === "B").length,
    C: searched.filter((p) => p.abcClass === "C").length,
  };

  const filtered = classFilter === "all" ? searched : searched.filter((p) => p.abcClass === classFilter);

  // Null vrijednosti (zaliha/pokrivenost bez podatka) uvijek idu na kraj, bez obzira na smjer.
  const rows = [...filtered].sort((a, b) => {
    const av = sortValue(a, sortKey);
    const bv = sortValue(b, sortKey);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return sortDir === "desc" ? bv - av : av - bv;
  });

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function toggleExpanded(productId: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }

  return (
    <div className="ring-1 ring-foreground/10">
      <div className="px-3 py-2.5 border-b flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-xs font-medium">Svi proizvodi ({products.length})</span>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pretraži proizvode…"
          className="h-7 px-2 w-full sm:w-56 text-xs border border-foreground/15 rounded-md bg-transparent text-foreground"
        />
        <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
          {(["all", "A", "B", "C"] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setClassFilter(c)}
              className={cn(
                "px-2 py-0.5 text-xs font-medium rounded-md border border-foreground/15",
                classFilter === c ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {c === "all" ? "Sve" : c} ({counts[c]})
            </button>
          ))}
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-2 text-left font-medium uppercase">Proizvod</th>
              {COLUMNS.map((col) => (
                <th key={col.key} className="p-2 text-right font-medium uppercase whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    className={cn("uppercase hover:text-foreground", sortKey === col.key ? "text-foreground" : "text-muted-foreground")}
                  >
                    {col.label}
                    {sortKey === col.key && <span className="ml-1">{sortDir === "desc" ? "↓" : "↑"}</span>}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="p-6 text-center text-muted-foreground">Nema rezultata.</td>
              </tr>
            ) : (
              rows.map((p) => {
                const hasVariations = p.variations.length > 0;
                const isOpen = expanded.has(p.productId);
                return [
                  <tr
                    key={p.productId}
                    // Cijeli red je klikabilan mišem; pristupačna kontrola je chevron dugme
                    // u ćeliji (red zadržava prirodnu `row` ulogu u tabeli).
                    onClick={hasVariations ? () => toggleExpanded(p.productId) : undefined}
                    className={cn("border-b transition-colors hover:bg-muted/50", hasVariations && "cursor-pointer")}
                  >
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <span className="w-4 shrink-0 text-muted-foreground">
                          {hasVariations && (
                            <button
                              type="button"
                              aria-expanded={isOpen}
                              aria-label={`${isOpen ? "Sakrij" : "Prikaži"} varijacije — ${p.name}`}
                              onClick={(e) => {
                                e.stopPropagation(); // Da klik na dugme ne okine i handler reda.
                                toggleExpanded(p.productId);
                              }}
                              className="flex items-center justify-center text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/40"
                            >
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </span>
                        {p.imageUrl ? (
                          <img src={p.imageUrl} alt="" className="w-8 h-8 shrink-0 object-cover ring-1 ring-foreground/10" />
                        ) : (
                          <div className="w-8 h-8 shrink-0 bg-foreground/10 ring-1 ring-foreground/10" />
                        )}
                        <span
                          className={cn(
                            "w-4 h-4 shrink-0 rounded-full flex items-center justify-center text-[9px] font-semibold text-white",
                            CLASS_DOT[p.abcClass]
                          )}
                        >
                          {p.abcClass}
                        </span>
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium truncate">{p.name}</span>
                          {p.sku && <span className="text-[10px] text-muted-foreground truncate">{p.sku}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-right tabular-nums">{fmtNumber(p.revenue)}</td>
                    <td className="p-2 text-right tabular-nums">{fmtNumber(p.quantity)}</td>
                    <td className="p-2 text-right tabular-nums text-muted-foreground">{p.cumulativeShare.toFixed(1)}%</td>
                    <td className={cn("p-2 text-right tabular-nums", p.stock === 0 && "text-red-500 font-medium")}>
                      {p.stock === null ? "—" : fmtNumber(p.stock)}
                    </td>
                    <td className="p-2 text-right tabular-nums whitespace-nowrap">
                      {p.coverageDays === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="inline-flex items-center justify-end gap-1.5">
                          <span className={cn("w-1.5 h-1.5 shrink-0 rounded-full", coverageTone(p.coverageDays))} />
                          {Math.round(p.coverageDays)} d
                        </span>
                      )}
                    </td>
                  </tr>,
                  ...(hasVariations && isOpen
                    ? [
                        <tr key={`${p.productId}-vars`} className="border-b bg-foreground/[0.02]">
                          <td colSpan={COLUMNS.length + 1} className="px-2 py-2 pl-10">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-foreground/10 text-muted-foreground">
                                  <th className="p-1.5 text-left font-medium uppercase">Varijacija</th>
                                  <th className="p-1.5 text-left font-medium uppercase">SKU</th>
                                  <th className="p-1.5 text-right font-medium uppercase">Prodano</th>
                                  <th className="p-1.5 text-right font-medium uppercase">Prihod</th>
                                  <th className="p-1.5 text-right font-medium uppercase">Zaliha</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.variations.map((v) => (
                                  <tr key={v.variationId}>
                                    <td className="p-1.5">{v.name ?? v.sku ?? "—"}</td>
                                    <td className="p-1.5 text-muted-foreground">{v.sku ?? "—"}</td>
                                    <td className="p-1.5 text-right tabular-nums">{fmtNumber(v.quantity)}</td>
                                    <td className="p-1.5 text-right tabular-nums">{fmtNumber(v.revenue)}</td>
                                    <td className={cn("p-1.5 text-right tabular-nums", v.stock === 0 && "text-red-500 font-medium")}>
                                      {v.stock === null ? "—" : fmtNumber(v.stock)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>,
                      ]
                    : []),
                ];
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
